import React, { useEffect, useRef } from "react";
import linkifyHtml from "linkify-html";
import { position } from "caret-pos";

import useToggleState from "./use_toggle";
import useUndoRedo from "./use_undo_redo";

import './autolink_textarea.css'

const LINKIFY_OPTIONS = { defaultProtocol: "https", attributes: { rel: "noopener", target: "_blank", class: "likified-link" } };

export default function AutoLinkTextarea(props) {
  const { name, value, onChange, placeholder } = props;
  const caretPosition = useRef(0);
  const editorRef = useRef(null);
  const editorState = useToggleState(false);
  const undoRedo = useUndoRedo();
  const isLastEventUndoable = useRef(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current === document.activeElement) {
      position(editorRef.current, caretPosition.current);
      if (isLastEventUndoable.current) undoRedo.push({ pos: caretPosition.current, value: editorRef.current.innerText });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function onInputEvent(event) {
    const newEvent = {
      target: {
        name,
        value: event.target.innerText,
      },
    };
    onChange(newEvent);
  }

  function onKeyDownEvent(event) {
    if (event.ctrlKey && event.key.toUpperCase() === "Z") {
      let currentValue;

      if (event.shiftKey) {
        currentValue = undoRedo.redo();
      } else {
        currentValue = undoRedo.undo();
      }

      if (currentValue) {
        caretPosition.current = currentValue.pos;
        const newEvent = {
          target: {
            name,
            value: currentValue.value,
          },
        };
        onChange(newEvent);
      }
      return;
    }

    const { pos } = position(event.target);

    // highlighted texts
    const selection = window.getSelection();
    const { startOffset = 0, endOffset = 0 } = (selection && selection.getRangeAt(0)) || {};
    const selectedTexts = endOffset - startOffset;

    isLastEventUndoable.current = true;

    switch (event.key) {
      case "Delete":
        caretPosition.current = pos;
        if (selectedTexts >= 1) caretPosition.current = caretPosition.current - (selectedTexts - 1) - 1;
        break;
      case "Backspace":
        caretPosition.current = pos - 1;
        if (selectedTexts >= 1) caretPosition.current = caretPosition.current - (selectedTexts - 1);
        break;
      default:
        caretPosition.current = pos + 1;
        if (selectedTexts >= 1) caretPosition.current = caretPosition.current - selectedTexts;
        isLastEventUndoable.current = false;
        break;
    }
  }

  function onClickEvent() {
    // delays make sure the editor is already in the editable state before setting it to a focused state
    // and also make the links act as links when clicked or hover onto.
    setTimeout(() => editorState.toggleOn(), 10);
    setTimeout(() => editorRef.current && editorRef.current.focus(), 20);
  }

  function onPasteEvent(event) {
    const pastedText = event.clipboardData.getData("Text");
    caretPosition.current = caretPosition.current + pastedText.length - 1;
    isLastEventUndoable.current = true;
  }

  function onFocusEvent(event) {
    // initialize undo stack on first focus of textarea,
    // because there is delay of 'value' during mount when the 'value' is dependent on backend/async response
    if (undoRedo.stack.length === 0) {
      event.target.value = "" // fix for the issue when the link is clicked and the textarea is focused the first time.
      const { pos } = position(event.target);
      undoRedo.push({ pos, value });
    }
  }

  function onBlurEvent(event) {
    editorState.toggleOff();

    if (undoRedo.current.value && undoRedo.current.value !== value) {
      const { pos } = position(event.target);
      undoRedo.push({ pos, value });
    }
  }

  const linkifiedText = linkifyHtml(value || "", LINKIFY_OPTIONS);

  return (
    <div
      ref={editorRef}
      className="form-control auto-link-textarea"
      dangerouslySetInnerHTML={{ __html: linkifiedText }}
      contentEditable={editorState.isToggled}
      onClick={onClickEvent}
      onInput={onInputEvent}
      onKeyDown={onKeyDownEvent}
      onPaste={onPasteEvent}
      onFocus={onFocusEvent}
      onBlur={onBlurEvent}
      placeholder={placeholder}
    ></div>
  );
}



// WEBPACK FOOTER //
// ./src/components/autolink_textarea/autolink_textarea.js